import MUICheckbox, { CheckboxProps as MuiCheckboxProps } from '@mui/material/Checkbox';
import { FormControlLabel, FormControlLabelProps } from '@mui/material';
import { forwardRef, HTMLAttributes, ReactElement } from 'react';
import styles from './Checkbox.module.scss';
import { CheckedIcon } from '../../../lib/icons/CheckedIcon';
import { UncheckedIcon } from '../../../lib/icons/UncheckedIcon';
import { classes, ClassType } from '../../../lib/utils/cssUtils';
import Loader from '../Loader/Loader';

type CheckboxVariant = 'default' | 'primary';

export type CheckboxProps = {
  label: string | ReactElement;
  selected: boolean;
  onSelect: (value: boolean) => void;
  className?: ClassType;
  customClasses?: {
    label?: ClassType;
  };
  checkboxProps?: MuiCheckboxProps;
  variant?: CheckboxVariant;
  disabled?: boolean;
  isLoading?: boolean;
} & Omit<HTMLAttributes<HTMLLabelElement>, 'onSelect'>;

const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
  (
    {
      label,
      selected,
      onSelect,
      className,
      customClasses,
      checkboxProps,
      disabled,
      variant = 'default',
      isLoading,
      ...rest
    },
    ref,
  ) => {
    const handleChange: FormControlLabelProps['onChange'] = (_event, state) => {
      onSelect(state);
    };

    return (
      <FormControlLabel
        {...rest}
        aria-busy={isLoading}
        className={classes(className)}
        ref={ref}
        classes={{
          label: classes(styles.label, customClasses?.label),
          root: classes(
            styles.root,
            className,
            (disabled || isLoading) && styles.disabled,
            variant === 'primary' && styles.primary,
            selected && variant === 'primary' && styles.labelPrimarySelected,
          ),
        }}
        onChange={handleChange}
        control={
          <MUICheckbox
            disabled={disabled || isLoading}
            className={styles.checkbox}
            checked={selected}
            checkedIcon={<CheckedIcon />}
            icon={<UncheckedIcon />}
            {...checkboxProps}
          />
        }
        label={
          <>
            {label}
            {isLoading && (
              <Loader
                className={classes(styles.loader, { [styles.loaderSelected]: selected })}
                containerProps={{
                  className: styles.loaderWrapper,
                }}
              />
            )}
          </>
        }
      />
    );
  },
);

export default Checkbox;
